import { useEffect } from "react";
import { Link, useParams } from "react-router-dom";
import { Printer, ShieldCheck } from "lucide-react";
import { LEGAL_NAV, LEGAL_PAGES } from "../data/legal";
import { setMeta } from "../lib/seo";

export default function LegalPage() {
  const { slug } = useParams();
  const page = LEGAL_PAGES[slug];

  useEffect(() => {
    if (page) setMeta(`${page.title} | ShoppinMagic`, page.intro.slice(0, 155));
  }, [page]);

  if (!page) {
    return (
      <div className="mx-auto max-w-4xl px-4 py-20 text-center" data-testid="legal-not-found">
        <h1 className="font-heading text-2xl font-bold text-slate-900">Page not found</h1>
        <Link to="/" className="mt-4 inline-block rounded-xl bg-gold-600 px-6 py-3 text-sm font-bold text-white">Back to homepage</Link>
      </div>
    );
  }

  return (
    <div data-testid={`legal-page-${slug}`} className="mx-auto max-w-7xl px-4 py-8 sm:px-6 lg:px-8">
      <div className="flex gap-10">
        <aside className="hidden w-64 shrink-0 lg:block">
          <div className="sticky top-24 space-y-5">
            <nav className="rounded-xl border border-slate-200 bg-white p-4 shadow-sm" aria-label="Legal pages">
              <p className="text-xs font-bold uppercase tracking-wider text-slate-400">Legal</p>
              <ul className="mt-2 space-y-1">
                {LEGAL_NAV.map((l) => (
                  <li key={l.slug}>
                    <Link
                      to={`/legal/${l.slug}`}
                      data-testid={`legal-nav-${l.slug}`}
                      className={`block rounded-lg px-3 py-2 text-sm font-semibold ${
                        l.slug === slug ? "bg-gold-50 text-gold-700" : "text-slate-600 hover:bg-slate-50"
                      }`}
                    >
                      {l.title}
                    </Link>
                  </li>
                ))}
              </ul>
            </nav>
            <nav className="rounded-xl border border-slate-200 bg-white p-4 shadow-sm" aria-label="Sections">
              <p className="text-xs font-bold uppercase tracking-wider text-slate-400">On this page</p>
              <ul className="mt-2 max-h-72 space-y-1 overflow-y-auto">
                {page.sections.map((s) => (
                  <li key={s.id}>
                    <a href={`#${s.id}`} className="block rounded px-2 py-1 text-xs text-slate-500 hover:text-gold-600">
                      {s.heading}
                    </a>
                  </li>
                ))}
              </ul>
            </nav>
          </div>
        </aside>

        <article className="min-w-0 max-w-3xl flex-1">
          <div className="flex items-center gap-2 text-xs font-semibold text-emerald-700">
            <ShieldCheck size={15} /> Plain-language, compliance-first
          </div>
          <h1 className="font-heading mt-2 text-3xl font-extrabold tracking-tight text-slate-900 sm:text-4xl">{page.title}</h1>
          <div className="mt-2 flex items-center gap-4 text-xs text-slate-400">
            <span>Last updated: {page.updated}</span>
            <button
              onClick={() => window.print()}
              data-testid="legal-print-button"
              className="flex items-center gap-1 rounded-lg border border-slate-200 bg-white px-2.5 py-1 font-semibold text-slate-600 hover:bg-slate-50"
            >
              <Printer size={12} /> Print
            </button>
          </div>
          <p className="mt-5 rounded-xl border border-slate-200 bg-white p-4 text-sm leading-relaxed text-slate-600 shadow-sm">
            {page.intro}
          </p>

          {page.sections.map((s) => (
            <section key={s.id} id={s.id} className="mt-8 scroll-mt-24">
              <h2 className="font-heading text-lg font-bold text-slate-900 sm:text-xl">{s.heading}</h2>
              {s.paragraphs?.map((p, i) => (
                <p key={i} className="mt-3 text-sm leading-relaxed text-slate-600 sm:text-base">{p}</p>
              ))}
              {s.bullets && (
                <ul className="mt-3 list-disc space-y-2 pl-5 text-sm leading-relaxed text-slate-600 sm:text-base">
                  {s.bullets.map((b, i) => <li key={i}>{b}</li>)}
                </ul>
              )}
            </section>
          ))}
        </article>
      </div>
    </div>
  );
}
